
import { supabase } from "@/integrations/supabase/client";
import { categorySyncService } from "./categorySyncService";
import { categoryOperationsService } from "./categoryOperations.service";
import { networkService } from "@/services/network.service";
import { UserCategory } from "@/types";
import OfflineLogger from "@/utils/offlineLogger";
import { getCachedUserId, setCachedUser } from "@/services/auth/sessionCache";

export class CategorySpecificSyncService {
  private async getCurrentUserId(): Promise<string | null> {
    try {
      const cachedId = await getCachedUserId();
      if (cachedId) {
        return cachedId;
      }

      const { data: userData, error } = await supabase.auth.getUser();
      if (!error && userData.user) {
        setCachedUser(userData.user);
        return userData.user.id;
      }

      return null;
    } catch (error) {
      OfflineLogger.error('CategorySpecificSyncService.getCurrentUserId', error);
      return null;
    }
  }

  async syncOnlyCategories(): Promise<UserCategory[]> {
    if (!networkService.isOnline()) {
      OfflineLogger.info('CategorySpecificSyncService.syncOnlyCategories', 'Offline mode, loading local categories');
      return categorySyncService.loadLocalCategories();
    }

    try {
      OfflineLogger.info('CategorySpecificSyncService.syncOnlyCategories', 'Starting category-specific sync');
      
      // Prima sincronizza le operazioni pending delle categorie
      await categorySyncService.syncPendingOperations();
      
      // Poi scarica le categorie aggiornate dal server
      const syncedCategories = await categorySyncService.syncFromServer();
      
      // Emetti evento specifico per la sincronizzazione delle categorie
      window.dispatchEvent(new CustomEvent('categoriesSyncCompleted', {
        detail: { categories: syncedCategories, timestamp: Date.now(), source: 'category-specific' }
      }));
      
      OfflineLogger.info('CategorySpecificSyncService.syncOnlyCategories', 'Category-specific sync completed successfully');
      return syncedCategories;
    } catch (error) {
      OfflineLogger.error('CategorySpecificSyncService.syncOnlyCategories', error);
      return categorySyncService.loadLocalCategories();
    }
  }

  async getPendingCategoriesCount(): Promise<number> {
    return categoryOperationsService.getPendingOperationsCount();
  }

  async hasPendingCategoryOperations(): Promise<boolean> {
    const count = await this.getPendingCategoriesCount();
    return count > 0;
  }
}

export const categorySpecificSyncService = new CategorySpecificSyncService();
